/* eslint-disable no-param-reassign */
import type { Image } from "canvas";
import { runGeneratorSync, runGeneratorAsync } from "../utils/run-generator.ts";
import getRenderMeasurements from "./get-render-measurements.ts";
import { renderPictureAsBricks } from "./render-picture-as-bricks.ts";
import type { DrawBounds } from "./draw-bounds.ts";
import renderPictureAsPixels from "./render-picture-as-pixels.ts";
import type { Bitmap } from "../bitmap/types.ts";
import type { CanvasWithSourceDimensions } from "./canvas-types.ts";
import type { BuildInput } from "./build-input.ts";
import { normaliseBuildInput } from "./build-input.ts";

// TODO: Remove references to createCanvas since can't use that in worker

const pixelRenderThreshold = 3;

function* renderPictureGenerator(
	ctx: Parameters<typeof renderPictureAsPixels>[0] &
		Parameters<typeof renderPictureAsBricks>[0] &
		CanvasWithSourceDimensions,
	buildInput: BuildInput,
	utilityCtx: Parameters<typeof renderPictureAsBricks>[5] &
		Parameters<typeof renderPictureAsPixels>[1],
	explicitDrawBounds?: DrawBounds,
): Generator<undefined, void> {
	const build = normaliseBuildInput(buildInput);
	const drawBounds = explicitDrawBounds ?? {
		x: 0,
		y: 0,
		width: ctx.canvas.width,
		height: ctx.canvas.height,
	};

	const { brickSizePixels, ceilBrickSizePixels } = getRenderMeasurements(
		drawBounds.width,
		drawBounds.height,
		build.width,
		build.height,
	);

	if (brickSizePixels <= pixelRenderThreshold) {
		yield* renderPictureAsPixels(ctx, utilityCtx, build, drawBounds);
		return;
	}
	if (ceilBrickSizePixels !== brickSizePixels) {
		console.warn(
			"Rendering bricks to non-integer size",
			brickSizePixels,
			ceilBrickSizePixels,
		);
	}
	yield* renderPictureAsBricks(
		ctx,
		build,
		brickSizePixels,
		ceilBrickSizePixels,
		drawBounds,
		utilityCtx,
	);
}

type PictureWithWatermarkOptions = {
	readonly showPoweredBy?: string;
};

function* renderPictureWithWatermarkGenerator(
	ctx: Parameters<typeof renderPictureGenerator>[0] &
		CanvasDrawImage &
		CanvasText &
		CanvasTextDrawingStyles &
		CanvasFillStrokeStyles,
	build: BuildInput,
	utilityCtx: Parameters<typeof renderPictureGenerator>[2],
	// Originally passed in string, but couldn't get heavy process runner to work with async generator
	// which was needed for loadImage
	// Was passing string in because project-core doesn't currently bundle any assets.
	logo: CanvasImageSource | Image,
	{ showPoweredBy }: PictureWithWatermarkOptions,
): Generator<undefined, void> {
	const logoSize = Math.round(
		Math.min(ctx.canvas.width, ctx.canvas.height) * 0.22,
	);
	yield* renderPictureGenerator(ctx, build, utilityCtx);
	const borderSize = Math.round(logoSize * 0.15);

	const poweredByGap = 10;
	const poweredByFontSize = 14;
	let logoY = ctx.canvas.height - logoSize - borderSize;
	if (showPoweredBy) {
		logoY -= poweredByGap + poweredByFontSize;
	}
	ctx.drawImage(
		logo as CanvasImageSource,
		borderSize,
		logoY,
		logoSize,
		logoSize,
	);
	if (showPoweredBy) {
		ctx.textAlign = "left";
		ctx.textBaseline = "top";
		ctx.fillStyle = "black";
		ctx.font = `${poweredByFontSize}px sans-serif`;
		ctx.strokeText(showPoweredBy, borderSize, logoY + logoSize + poweredByGap);
	}
}

function renderPictureWithWatermarkSync(
	ctx: Parameters<typeof renderPictureWithWatermarkGenerator>[0],
	image: Bitmap,
	utilityCtx: Parameters<typeof renderPictureWithWatermarkGenerator>[2],
	logo: CanvasImageSource | Image,
	options: PictureWithWatermarkOptions,
) {
	const generator = renderPictureWithWatermarkGenerator(
		ctx,
		image,
		utilityCtx,
		logo,
		options,
	);
	runGeneratorSync(generator);
}

type RenderPictureOptions = {
	readonly drawBounds?: DrawBounds;
};

function renderPictureSync(
	ctx: Parameters<typeof renderPictureGenerator>[0],
	build: BuildInput,
	utilityCtx: Parameters<typeof renderPictureGenerator>[2],
	options?: RenderPictureOptions,
) {
	const generator = renderPictureGenerator(
		ctx,
		build,
		utilityCtx,
		options?.drawBounds,
	);
	runGeneratorSync(generator);
}

type AsyncOptions = {
	readonly abortSignal: AbortSignal;
};

async function renderPictureAsync(
	ctx: Parameters<typeof renderPictureGenerator>[0],
	build: BuildInput,
	utilityCtx: Parameters<typeof renderPictureGenerator>[2],
	renderOptions?: RenderPictureOptions,
	options?: AsyncOptions,
) {
	const generator = renderPictureGenerator(
		ctx,
		build,
		utilityCtx,
		renderOptions?.drawBounds,
	);
	// TODO: Not sure why typing doesn't work
	// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
	await runGeneratorAsync(generator, options as any);
}

export type { RenderPictureOptions, PictureWithWatermarkOptions };
export {
	renderPictureSync,
	renderPictureAsync,
	renderPictureWithWatermarkSync,
	pixelRenderThreshold,
	renderPictureGenerator,
	renderPictureWithWatermarkGenerator,
};
